import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import debounce from 'lodash/debounce';

import { Collection } from 'core/model/Collection';
import Box from 'core/components/Box';
import Button from 'core/components/Button';
import Flex from 'core/components/Flex';
import InputGroup from 'core/form/components/InputGroup';

@observer
class Search extends Component {
  static propTypes = {
    // array of strings, filters to exclude from showing
    excludeFilters: PropTypes.array,
    autoFocus: PropTypes.bool,
    clearSelectionsOnChange: PropTypes.bool,
    collection: PropTypes.instanceOf(Collection).isRequired,
    inputProps: PropTypes.object,
    placeholder: PropTypes.string,
    onChange: PropTypes.func
  };

  static defaultProps = {
    excludeFilters: [],
    autoFocus: true,
    clearSelectionsOnChange: false,
    placeholder: 'Search',
    inputProps: {},
    onChange: null
  };

  state = { filterValue: '', hasUserTyped: false };

  // The input is controlled; when the user types we keep their value. Otherwise we surface
  // the latest observable values (searchTerm prop, collection searchState/filterState).
  get displayValue() {
    const { searchTerm, collection } = this.props;
    const { filterValue, hasUserTyped } = this.state;

    if (hasUserTyped) {
      return filterValue ?? '';
    }

    return searchTerm ?? collection.searchState?.value ?? collection.filterState ?? '';
  }

  debouncedSearchAction = debounce(() => {
    const { collection, onChange } = this.props;
    const value = this.displayValue;

    if (onChange) {
      onChange(value);
    } else {
      // we expect the parent to handle this if providing its own change handler
      collection.filter(value);
    }
  }, 300);

  componentWillUnmount() {
    // Cancel any pending debounced operations to prevent memory leaks
    if (this.debouncedSearchAction) {
      this.debouncedSearchAction.cancel();
    }
  }

  handleSearchChange = (e) => {
    this.setState(
      () => ({ filterValue: e.target.value, hasUserTyped: true }),
      () => this.debouncedSearchAction()
    );
  };

  handleSearchClear = () => {
    const { collection, clearSelectionsOnChange, onChange } = this.props;

    // Clear value but keep hasUserTyped true so control stays with the user after first input
    this.setState({ filterValue: '' });

    if (onChange) {
      onChange('');
    } else {
      collection.filter('');
    }

    if (clearSelectionsOnChange) {
      collection.clearSelection();
    }
  };

  render() {
    const {
      autoFocus,
      clearSelectionsOnChange,
      collection,
      excludeFilters,
      inputProps,
      placeholder,
      onChange,
      ...rest
    } = this.props;
    const value = this.displayValue;

    const rightElement = value ? (
      <Flex height="100%" mr="5px">
        <Button icon="cross" onClick={this.handleSearchClear} minimal />
      </Flex>
    ) : undefined;

    return (
      <Box className="admin-table-search" {...rest}>
        <InputGroup
          value={value}
          autoFocus={autoFocus}
          leftIcon={inputProps.leftElement ? undefined : 'search'}
          onChange={this.handleSearchChange}
          placeholder={placeholder}
          rightElement={rightElement}
          {...inputProps}
        />
      </Box>
    );
  }
}

export default Search;
